import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpsertScheduleRequest } from '@functieloonhuis/shared';
import { workScheduleApi } from '../../../lib/workScheduleApi';

export function useEmployeeSchedule(employeeId: number) {
  return useQuery({
    queryKey: ['work-schedule', employeeId],
    queryFn: () => workScheduleApi.getByEmployee(employeeId),
    enabled: !!employeeId,
  });
}

export function useCurrentWeekType() {
  return useQuery({
    queryKey: ['work-schedules', 'current-week'],
    queryFn: () => workScheduleApi.getCurrentWeek(),
    staleTime: 1000 * 60 * 60, // 1 hour cache
  });
}

export function useUpsertSchedule(employeeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertScheduleRequest) =>
      workScheduleApi.upsertSchedule(employeeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedule', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['work-schedules'] });
    },
  });
}

export function useTeamSchedules() {
  return useQuery({
    queryKey: ['work-schedules', 'team'],
    queryFn: () => workScheduleApi.getTeamSchedules(),
    staleTime: 1000 * 60 * 5, // 5 min cache
  });
}

export function useAllSchedules() {
  return useQuery({
    queryKey: ['work-schedules', 'all'],
    queryFn: () => workScheduleApi.getAllSchedules(),
    staleTime: 1000 * 60 * 5, // 5 min cache
  });
}

export function useHolidays(year: number) {
  return useQuery({
    queryKey: ['holidays', year],
    queryFn: () => workScheduleApi.getHolidays(year),
    staleTime: 1000 * 60 * 60, // 1 hour cache
  });
}

export function useDeleteSchedule(employeeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => workScheduleApi.deleteSchedule(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedule', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['work-schedules'] });
    },
  });
}
